import { useTranslation } from 'react-i18next';
import type { CatalogRow } from '@/lib/catalog';

export type { CatalogRow } from '@/lib/catalog';

interface Props {
  rows: CatalogRow[];
  onOpen: (sku: string) => void;
}

const n0 = (v: number | null) => (v == null ? '—' : v.toFixed(0));

export default function ProductGrid({ rows, onOpen }: Props) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {rows.map((p) => (
        <article
          key={p.sku}
          onClick={() => onOpen(p.sku)}
          className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
        >
          <div className="relative h-[168px] w-full overflow-hidden bg-background-100">
            {p.image ? (
              <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-contain object-center transition-transform duration-300 group-hover:scale-[1.02]" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-[11px] text-foreground-400">{t('onb.noImage')}</div>
            )}
            <div className="absolute left-2 top-2 flex flex-col gap-1">
              {p.stock > 0 ? (
                <span className="rounded bg-background-50/95 px-1.5 py-0.5 text-[10px] font-semibold text-primary-800">{t('catalog.inStock', { count: p.stock })}</span>
              ) : (
                <span className="rounded bg-accent-100/95 px-1.5 py-0.5 text-[10px] font-semibold text-accent-900">{t('catalog.incoming', { qty: p.incomingQty })}</span>
              )}
            </div>
            <span className={`absolute right-2 top-2 h-2 w-2 rounded-full ${p.published ? 'bg-primary-500' : 'bg-background-400'}`} title={p.published ? t('status.Published') : t('status.Hidden')} />
          </div>

          <div className="flex flex-1 flex-col p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="tnum shrink-0 text-[10.5px] font-medium text-foreground-400">{p.sku}</p>
              <span className="truncate rounded border border-background-200 bg-background-100 px-1 py-0.5 text-[9.5px] font-medium text-foreground-500">
                {p.source === 'Own' ? t('status.Own warehouse') : t('status.Partner')}
              </span>
            </div>
            <h3 className="mt-1 line-clamp-2 min-h-[34px] text-[12.5px] font-medium leading-[17px] text-foreground-900">{p.name}</h3>
            <p className="mt-0.5 truncate text-[10.5px] text-foreground-400">
              {t(`category.${p.category}`, { defaultValue: p.category })}
              {p.color ? ` · ${p.color}` : ''}
              {p.base ? ` · ${p.base}` : ''}
            </p>

            <div className="mt-2.5 flex items-end justify-between border-t border-background-200 pt-2.5">
              <div>
                <p className="tnum text-[9.5px] uppercase tracking-[0.08em] text-foreground-400">{t('catalog.costLabel', { value: n0(p.cost) })}</p>
                <p className="tnum text-[14px] font-semibold text-foreground-950">AED {n0(p.sale)}</p>
              </div>
              {p.marginPct != null && <span className="rounded bg-secondary-50 px-1.5 py-0.5 text-[10px] font-semibold text-secondary-800">{t('catalog.marginPct', { value: p.marginPct.toFixed(0) })}</span>}
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}
